import { Fragment, useEffect, useReducer, useRef, useState } from 'react';
import { Icon } from '@ab-inbev-maz/compass';
import { useCanvasEditor } from './CanvasEditorContext';
import { playObjectAnimation, type AnimatableObject } from './CanvasAnimations';
import {
  getLayerId,
  getLayerName,
  getLayerObjects,
  getLayerType,
  isLayerLocked,
  reorderLayersFromDisplayOrder,
  setLayerHidden,
  setLayerLocked,
  type LayerObject,
} from './CanvasLayers';

const SUB_TABS = [
  { id: 'capas', label: 'Capas' },
  { id: 'historial', label: 'Historial' },
] as const;

type SubTabId = (typeof SUB_TABS)[number]['id'];

const ACTION_BUTTON_CLASSNAME =
  'w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent';

interface LayerRowProps {
  object: LayerObject;
  isActive: boolean;
  onSelect: () => void;
  onDragStartLayer: () => void;
  onDragOverLayer: (e: React.DragEvent) => void;
  onDropLayer: (e: React.DragEvent) => void;
  onDragEndLayer: () => void;
  onChange: () => void;
  onRecordHistory: (label: string) => void;
}

// A layer row: double-clicking the name makes it editable (only renames the layer,
// never the actual text content when the element is text).
const LayerRow = ({
  object,
  isActive,
  onSelect,
  onDragStartLayer,
  onDragOverLayer,
  onDropLayer,
  onDragEndLayer,
  onChange,
  onRecordHistory,
}: LayerRowProps) => {
  const { canvas } = useCanvasEditor();
  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(getLayerName(object));
  const inputRef = useRef<HTMLInputElement>(null);

  const hasAnimation = !!(object as AnimatableObject).animationConfig?.type;
  const hidden = object.visible === false;
  const locked = isLayerLocked(object);

  useEffect(() => {
    if (isRenaming) inputRef.current?.focus();
  }, [isRenaming]);

  const commitRename = () => {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== getLayerName(object)) {
      object.layerName = trimmed;
      onRecordHistory(`Renombrar capa a "${trimmed}"`);
    }
    setIsRenaming(false);
    onChange();
  };

  return (
    <div
      draggable
      onDragStart={onDragStartLayer}
      onDragOver={onDragOverLayer}
      onDrop={onDropLayer}
      onDragEnd={onDragEndLayer}
      className={`flex items-center gap-2 px-2 py-2 rounded-md border ${
        isActive ? 'bg-[#FFF9E5] border-[#FFCC00]' : 'border-transparent hover:bg-gray-50'
      }`}
    >
      <span className="text-gray-400 cursor-grab select-none" title="Arrastrar para reordenar">
        ⠿
      </span>

      {isRenaming ? (
        <>
          <span className="text-gray-500 shrink-0 text-sm">
            <Icon name={getLayerType(object) === 'text' ? 'functionalities/text' : 'media/image-add'} />
          </span>
          <input
          ref={inputRef}
          value={nameDraft}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') inputRef.current?.blur();
            if (e.key === 'Escape') {
              setNameDraft(getLayerName(object));
              setIsRenaming(false);
            }
          }}
          className="flex-1 min-w-0 h-6 px-1 text-sm border border-gray-300 rounded"
        />
        </>
      ) : (
        <button
          type="button"
          aria-pressed={isActive}
          onClick={onSelect}
          onDoubleClick={() => {
            setNameDraft(getLayerName(object));
            setIsRenaming(true);
          }}
          className="flex flex-1 min-w-0 items-center gap-2 text-left cursor-pointer"
          title={getLayerName(object)}
        >
          <span className="text-gray-500 shrink-0 text-sm">
            <Icon name={getLayerType(object) === 'text' ? 'functionalities/text' : 'media/image-add'} />
          </span>
          <span className={`flex-1 min-w-0 truncate text-sm ${isActive ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
            {getLayerName(object)}
          </span>
        </button>
      )}

      <button
        type="button"
        disabled={!hasAnimation}
        onClick={(e) => {
          e.stopPropagation();
          if (canvas && hasAnimation) playObjectAnimation(canvas, object as AnimatableObject);
        }}
        className={ACTION_BUTTON_CLASSNAME}
        title="Reproducir animación"
      >
        <Icon name="media/play" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setLayerHidden(object, !hidden);
          canvas?.requestRenderAll();
          onChange();
          onRecordHistory(hidden ? 'Mostrar elemento' : 'Ocultar elemento');
        }}
        className={ACTION_BUTTON_CLASSNAME}
        title={hidden ? 'Mostrar' : 'Ocultar'}
      >
        <Icon name={hidden ? 'system/eye-off' : 'system/eye-on'} />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setLayerLocked(object, !locked);
          onChange();
          onRecordHistory(locked ? 'Desbloquear elemento' : 'Bloquear elemento');
        }}
        className={ACTION_BUTTON_CLASSNAME}
        title={locked ? 'Desbloquear' : 'Bloquear'}
      >
        <Icon name={locked ? 'functionalities/lock' : 'functionalities/unlock'} />
      </button>
    </div>
  );
};

// "Layers" tab panel: lists the canvas elements from front to back,
// with animation playback, show/hide, position locking and drag & drop reordering.
const CanvasLayersTab = () => {
  const { canvas, selectedObject, recordHistory, historyEntries, historyIndex, jumpToHistory } = useCanvasEditor();
  const [subTab, setSubTab] = useState<SubTabId>('capas');
  const [, forceRefresh] = useReducer((tick: number) => tick + 1, 0);
  const [dragOverBoundary, setDragOverBoundary] = useState<number | null>(null);
  const draggedObjectRef = useRef<LayerObject | null>(null);

  // The panel keeps no source of truth of its own: it refreshes when the canvas objects
  // change (add/remove/select) and also manually after each of its own actions.
  useEffect(() => {
    if (!canvas) return;
    canvas.on('object:added', forceRefresh);
    canvas.on('object:removed', forceRefresh);
    canvas.on('selection:created', forceRefresh);
    canvas.on('selection:updated', forceRefresh);
    canvas.on('selection:cleared', forceRefresh);
    return () => {
      canvas.off('object:added', forceRefresh);
      canvas.off('object:removed', forceRefresh);
      canvas.off('selection:created', forceRefresh);
      canvas.off('selection:updated', forceRefresh);
      canvas.off('selection:cleared', forceRefresh);
    };
  }, [canvas]);

  if (!canvas) return null;

  // Front (top) to back (bottom), the reverse of fabric's internal array.
  const layerObjects = [...getLayerObjects(canvas)].reverse();

  // The index (0..length) represents the gap BETWEEN rows where the dragged element
  // would land: upper half of row `index` => gap `index`, lower half => `index + 1`.
  const computeBoundary = (index: number, e: React.DragEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const isTopHalf = e.clientY - rect.top < rect.height / 2;
    return isTopHalf ? index : index + 1;
  };

  const handleDragOver = (index: number, e: React.DragEvent) => {
    e.preventDefault();
    const boundary = computeBoundary(index, e);
    setDragOverBoundary((prev) => (prev === boundary ? prev : boundary));
  };

  const handleDragEnd = () => {
    draggedObjectRef.current = null;
    setDragOverBoundary(null);
  };

  const handleDrop = (index: number, e: React.DragEvent) => {
    e.preventDefault();
    const dragged = draggedObjectRef.current;
    const boundary = computeBoundary(index, e);
    handleDragEnd();
    if (!dragged) return;

    const sourceIndex = layerObjects.indexOf(dragged);
    if (sourceIndex === -1) return;

    const targetIndex = sourceIndex < boundary ? boundary - 1 : boundary;
    if (targetIndex === sourceIndex) return;

    const newOrder = layerObjects.filter((o) => o !== dragged);
    newOrder.splice(targetIndex, 0, dragged);
    reorderLayersFromDisplayOrder(canvas, newOrder);
    recordHistory(`Reordenar capa "${getLayerName(dragged)}"`);
    forceRefresh();
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex w-full border-b border-gray-300">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setSubTab(tab.id)}
            className={`flex-1 pb-2 text-sm font-semibold text-center border-b-4 cursor-pointer ${
              subTab === tab.id ? 'text-gray-900 border-[#FFCC00]' : 'text-[#595959] border-transparent'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subTab === 'historial' && historyEntries.length === 0 && (
        <p className="text-sm text-gray-500">Aún no hay cambios registrados.</p>
      )}
      {subTab === 'historial' && historyEntries.length > 0 && (
          <div className="flex flex-col gap-1">
            {[...historyEntries].reverse().map((entry, reversedIndex) => {
              const index = historyEntries.length - 1 - reversedIndex;
              const isCurrent = index === historyIndex;
              const isFuture = index > historyIndex;
              return (
                <button
                  key={entry.timestamp}
                  type="button"
                  onClick={() => jumpToHistory(index)}
                  className={`flex items-center justify-between px-2 py-1 rounded-md text-left cursor-pointer border ${
                    isCurrent ? 'bg-[#FFF9E5] border-[#FFCC00]' : 'border-transparent hover:bg-gray-50'
                  } ${isFuture ? 'opacity-40' : ''}`}
                >
                  <span className={`text-sm truncate ${isCurrent ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
                    {entry.label}
                  </span>
                  <span className="text-xs text-gray-400 shrink-0 ml-2">
                    {new Date(entry.timestamp).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </button>
              );
            })}
          </div>
      )}
      {subTab !== 'historial' && layerObjects.length === 0 && (
        <p className="text-sm text-gray-500">No hay elementos en el canvas.</p>
      )}
      {subTab !== 'historial' && layerObjects.length > 0 && (
        <div className="flex flex-col">
          {layerObjects.map((object, index) => (
            <Fragment key={getLayerId(object)}>
              <div className={`h-0.5 my-0.5 rounded-full ${dragOverBoundary === index ? 'bg-[#FFCC00]' : 'bg-transparent'}`} />
              <LayerRow
                object={object}
                isActive={object === selectedObject}
                onSelect={() => {
                  canvas.setActiveObject(object);
                  canvas.requestRenderAll();
                }}
                onDragStartLayer={() => {
                  draggedObjectRef.current = object;
                }}
                onDragOverLayer={(e) => handleDragOver(index, e)}
                onDropLayer={(e) => handleDrop(index, e)}
                onDragEndLayer={handleDragEnd}
                onChange={forceRefresh}
                onRecordHistory={recordHistory}
              />
            </Fragment>
          ))}
          <div
            className={`h-0.5 my-0.5 rounded-full ${
              dragOverBoundary === layerObjects.length ? 'bg-[#FFCC00]' : 'bg-transparent'
            }`}
          />
        </div>
      )}
    </div>
  );
};

export default CanvasLayersTab;
